import { InjectionToken } from '@angular/core';
import { UrlCodec, UrlCodecs } from './url-codecs';

export type FilterSchema<P> = { [K in keyof P]?: UrlCodec<NonNullable<P[K]>> };
export interface ParamReader {
  get(name: string): string | null;
}

/**
 * Owns one page's custom filter state and its URL mapping.
 * Subclass once per list page and declare the schema next to the DTO type.
 * `value` is a single stable object for the page's lifetime — components
 * read/mutate it, never reassign it.
 */
export abstract class ListFilters<P extends object> {
  abstract readonly schema: FilterSchema<P>;
  private readonly _value = {} as P;

  get value(): P {
    return this._value;
  }

  get keys(): (keyof P & string)[] {
    return Object.keys(this.schema) as (keyof P & string)[];
  }

  reset(): void {
    for (const k of Object.keys(this._value)) delete (this._value as any)[k];
  }

  isEmpty(): boolean {
    return this.keys.every(k => this._value[k] == null || (this._value[k] as unknown) === '');
  }

  serialize(): Record<string, string | null> {
    const out: Record<string, string | null> = {};
    for (const k of this.keys) out[k] = this.codec(k).to(this._value[k] as any);
    return out;
  }

  /** Mutates value in place. Returns true if anything changed. */
  applyFrom(qp: ParamReader): boolean {
    let changed = false;
    for (const k of this.keys) {
      const raw = qp.get(k);
      const next = raw === null ? undefined : this.codec(k).from(raw);
      if (!sameValue(this._value[k], next)) {
        (this._value as any)[k] = next;
        changed = true;
      }
    }
    return changed;
  }

  private codec(k: keyof P & string): UrlCodec<any> {
    return (this.schema[k] as UrlCodec<any>) ?? UrlCodecs.string;
  }
}

export const LIST_FILTERS = new InjectionToken<ListFilters<any>>('LIST_FILTERS');

function sameValue(a: unknown, b: unknown): boolean {
  if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime();
  if (a == null && b == null) return true;
  return JSON.stringify(a) === JSON.stringify(b);
}
